import React from 'react';
import { Lightbulb } from 'lucide-react';

interface LetterKeyInfoProps {
  darkMode: boolean;
}

interface LetterGroup {
  title: string;
  items: { code: string; label: string }[];
}

const LETTER_GROUPS: LetterGroup[] = [
  { title: 'Lettres principales', items: [{ code: 'RA', label: 'rachis' }, { code: 'AMP', label: 'amputation' }, { code: 'ARL', label: 'affections respiratoires' }, { code: 'RAV', label: 'affections vasculaires' }] },
  { title: '2e position', items: [{ code: 'S', label: 'membre supérieur' }, { code: 'I', label: 'membre inférieur' }] },
  { title: '3e position', items: [{ code: 'C', label: 'suite de chirurgie (ou O : opération)' }, { code: 'M', label: 'médical' }] }
];

const EXAMPLES: { code: string; description: string; decomposition: string }[] = [
  { code: 'RIM', description: 'Rééducation du Membre Inférieur non opéré soumis à référentiel.', decomposition: 'I = Inférieur, M = Médical' },
  { code: 'VSC', description: 'Rééducation du membre Supérieur opéré non soumis à référentiel.', decomposition: 'S = Supérieur, C = Chirurgie' }
];

const LetterKeyInfo: React.FC<LetterKeyInfoProps> = ({ darkMode }) => {
  const cardBg = darkMode ? 'bg-appines-petrol border-appines-white/12' : 'bg-appines-white border-[rgba(1,42,54,0.12)]';
  const titleText = darkMode ? 'text-appines-white' : 'text-appines-petrol';
  const bodyText = darkMode ? 'text-appines-white/65' : 'text-appines-petrol/80';
  const mutedText = darkMode ? 'text-appines-white/45' : 'text-appines-petrol/45';
  const sectionTitle = darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65';
  const borderColor = darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.12)]';
  const chipBg = darkMode ? 'bg-appines-emerald/15 text-appines-emerald' : 'bg-appines-glass/25 text-appines-petrol';
  const exampleBg = darkMode ? 'bg-appines-white/5' : 'bg-[rgba(1,42,54,0.04)]';

  return (
    <section className="py-12">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className={`text-3xl md:text-4xl font-bold mb-3 ${titleText}`}>Comprendre les lettres clés</h2>
          <p className={`text-base md:text-lg max-w-2xl mx-auto ${mutedText}`}>Décryptage des codes NGAP pour mieux comprendre la cotation de tes actes</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {LETTER_GROUPS.map((group) => (
            <div key={group.title} className={`flex flex-col rounded-xl border shadow-sm hover:shadow-md transition-all duration-200 p-6 ${cardBg}`}>
              <h3 className={`text-xs font-semibold uppercase tracking-wide mb-4 ${sectionTitle}`}>{group.title}</h3>
              <ul className="space-y-3">
                {group.items.map((item) => (
                  <li key={item.code} className="flex items-baseline gap-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono font-semibold ${chipBg}`}>{item.code}</span>
                    <span className={`text-sm ${bodyText}`}>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className={`rounded-xl border p-6 ${borderColor} ${exampleBg}`}>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className={`h-4 w-4 ${mutedText}`} aria-hidden="true" />
            <p className={`text-xs font-semibold uppercase tracking-wide ${sectionTitle}`}>Exemples pratiques</p>
          </div>
          <div className={`divide-y ${borderColor}`}>
            {EXAMPLES.map((example, index) => (
              <div key={example.code} className={`flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 ${index === 0 ? 'pb-4' : 'pt-4'}`}>
                <span className={`inline-flex items-center self-start px-2 py-0.5 rounded-md text-xs font-mono font-semibold ${chipBg}`}>{example.code}</span>
                <div className="flex-1">
                  <p className={`text-sm ${bodyText}`}>{example.description}</p>
                  <p className={`text-xs mt-1 ${mutedText}`}>{example.decomposition}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LetterKeyInfo;
